import {CustomAttributeDefinition} from '@api/graphql'
import {Icon} from '@blueprintjs/core'
import {Tooltip2, _} from '@api'
import {FilterStore, FilterItemDefinition, useFilterStore, SimpleFilterItemDefinition} from '@modules/catalog'
import {PrimaryIconButton} from 'centric-ui-components'
import {FilterGroupsItemsStore, useFilterGroupsItemsStore} from './'
import {useCatalog} from '@stores'
import {action, computed, observable} from 'mobx'
import * as css from './FilterCheckbox.css'
import {classNames, Highlighter, React, observer, useLocalStore, useAsObservableSource, useEffect} from '@api'

interface MyProps {
  name: string,
  checked: boolean
  excluded?: boolean
  count?: React.ReactNode,
  value?: any
  onChange?: (e, checked, item) => void
  showIcon?: boolean
  type?: string
  item?: SimpleFilterItemDefinition
  displayName?: string

  /**
   * The more dialog has its own independent search textbox
   */
  search?: string
  customAttributeDefinition?: CustomAttributeDefinition
  className?: string
  group?: string,
}

const domParser = new DOMParser()
const shouldProcessHTMLRegex = new RegExp('[\<\&]')

/**
 * This is very convoluted as the string can be something like &quote;thing&quote; white will be translated to a single text node in HTML with text "thing"
 * @param str
 */
const processHTML = _.memoize((str) => {
  const {body: {childNodes}} = domParser.parseFromString(str, 'text/html'),
    nodes = Array.from(childNodes),
    isHTML = nodes.length > 1 || nodes.some(node => node.nodeType !== 3)

  //console.log(str, r, childNodes, nodes.map(n => n.nodeType))
  return {isHTML , text: nodes.length == 1 ? nodes[0].textContent : str}
})

class FilterCheckboxStore {
  constructor(public props: Partial<MyProps>, public filter: FilterStore, public groupStore: FilterGroupsItemsStore) {
    this.checked = props.checked === true
    this.excluded = props.excluded === true
  }

  @computed get search() {
    const {props: {search}, filter} = this
    return search || filter.state.search
  }

  @computed get strings() { return this.filter.strings}

  @computed get displayText() {
    const {filter, props: {group, name}} = this
    return filter.labelFor({group, value: name})
  }

  @computed get searchWords() {
    return !this.props.search ? this.filter.localSearchWords : _.map(_.escapeRegExp(this.search).split(' ') ?? [])
  }

  @observable checked = false
  @observable excluded = false

  @computed get active() {
    const {props: {item}} = this
    return item && item.active !== undefined ? item.active : true
  }

  @action onChangeCheck = (e, exclude = null) => {
    let isExclude = exclude === null ? this.excluded : exclude

    if (!isExclude) {
      this.checked = !this.checked
      if (this.checked) {
        this.excluded = false
      }
    }
    else {
      this.excluded = !this.excluded
    }
    const {groupStore, filter, props: {item, group}} = this
    const groupToFilter = groupStore?.props?.group || group
    filter.toggleFilterBatched({key: groupToFilter, value: item.compoundId ?? item.id, item, exclude: isExclude})
  }

  @computed get htmlText() {
    const {displayText} = this
    if (displayText.match(shouldProcessHTMLRegex)) {
      return processHTML(displayText)
    }
    else {
      return { isHTML: false, text: displayText }
    }
  }

  @computed get catalogField () {
    return this.filter.fieldByName[this.props.type]
  }
}

export const FilterCheckbox = observer<MyProps>(function FilterCheckbox(props) {
    const groupStore = useFilterGroupsItemsStore(),
      filterStore = useFilterStore(),
      {imageUrlFor, labelForField} = useCatalog(),
      obsProps = useAsObservableSource(props),
      store = useLocalStore(() => new FilterCheckboxStore(obsProps, filterStore, groupStore)),
      {strings, searchWords, active, displayText, checked, excluded, onChangeCheck, htmlText, catalogField} = store,
      {count, className, value, displayName, showIcon, item} = props,
      {def} = groupStore?.filter?.groupFor(groupStore.props.group ?? props.type) ?? {}

    useEffect(() => {store.checked = props.checked === true}, [props.checked])
    useEffect(() => {store.excluded = props.excluded === true}, [props.excluded])

  if (count === 0 && def?.type !== 'Boolean') return null

  return <label className={classNames(css.root, className)} data-excluded={excluded}>
      <input type='checkbox' readOnly={excluded} value={value} checked={checked} onChange={e => onChangeCheck(e)}/>

      {!showIcon ? null
                 : item?.hex
                   ? <div className={css.hex} data-hex={item?.hex} style={{backgroundColor: item?.hex}}/>
                   : item?.images?.length
                     ? <img src={imageUrlFor(item?.images[0])} className={classNames(css.image, {[css.inactive]: !active})}/>
                     : <div className={classNames(css.placeholder, {[css.inactive]: !active})}>
                       {displayName ? displayName[0] : _.first(labelForField(groupStore.fieldDefinition))}
                     </div>}
      <span data-type='name' className={css.name}>
        {htmlText.isHTML ? <span className={css.highlighter} dangerouslySetInnerHTML={{__html: displayText}}/>
                     : <Highlighter className={css.highlighter} textToHighlight={htmlText.text} searchWords={searchWords}/>}
      </span>
      {count != null && <span className={css.count} data-type='count'>{_.isNumber(count) ? `(${count})` : count}</span>}
        <PrimaryIconButton tooltip={strings.negativeFilter} icon={<Icon icon='small-cross'/>} active={excluded} onClick={e => onChangeCheck(e, true)}/>

      <div className={css.checkboxIndicator}/>
    </label>
  }
)

FilterCheckbox.defaultProps = {showIcon: false}



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/filters/components/internal/FilterCheckbox.tsx